import axios from "../api/api";
import { API_ROUTES } from "./routes";

/**
 * Obtiene el resumen (mensual | diario) conmutando entre endpoints
 * de ADMIN vs CLIENTE según `mode`. Si el split no existe en back,
 * hace fallback al endpoint legacy y pasa `view`.
 */
export const fetchResumen = async (
  {
    tipo = "mensual", // 'mensual' | 'diario'
    empresa,
    cliente,
    fechaInicio,
    fechaFin,
    pais,
    troncal,
  },
  { mode = "user" } = {}
) => {
  const paramsBase = {
    ...(empresa && { empresa }),
    ...(cliente && { cliente }),
    fechaInicio,
    fechaFin,
    ...(pais && { pais }),
    ...(troncal && { troncal }),
  };

  const splitEndpoint =
    mode === "admin"
      ? tipo === "diario"
        ? API_ROUTES?.resumen?.admin?.diario
        : API_ROUTES?.resumen?.admin?.mensual
      : tipo === "diario"
      ? API_ROUTES?.resumen?.cliente?.diario
      : API_ROUTES?.resumen?.cliente?.mensual;

  const legacyEndpoint =
    tipo === "diario" ? API_ROUTES.resumenDiario : API_ROUTES.resumenMensual;

  if (splitEndpoint) {
    try {
      const qs = new URLSearchParams(paramsBase);
      const { data } = await axios.get(`${splitEndpoint}?${qs.toString()}`, {
        withCredentials: true,
      });
      return Array.isArray(data) ? data : [];
    } catch (err) {
      const s = err?.response?.status;
      if (s !== 404 && s !== 405) throw err;
    }
  }

  const qs2 = new URLSearchParams({ ...paramsBase, view: mode }); // 'admin' | 'user'
  const { data } = await axios.get(`${legacyEndpoint}?${qs2.toString()}`, {
    withCredentials: true,
  });
  return Array.isArray(data) ? data : [];
};

export const fetchReporteResumenGeneral = async () => {
  const { data } = await axios.get(`${API_ROUTES.reporteResumenGeneral}`, {
    withCredentials: true,
  });
  return data;
};

export const cargarTarifas = async () => {
  try {
    const { data } = await axios.get(API_ROUTES.tarifas, {
      withCredentials: true,
    });
    return {
      tarifasPorCliente: data.tarifasPorCliente,
      tarifasGenerales: data.tarifasGenerales,
    };
  } catch (error) {
    console.error("Error al cargar tarifas:", error);
    throw error;
  }
};

export const fetchEmpresasMonitor = async (q = "") => {
  const { data } = await axios.get(`${API_ROUTES.monitorEmpresas}?q=${q}`, {
    withCredentials: true,
  });
  return data.items;
};

export const fetchClientesMonitor = async (empresaId, q = "") => {
  const { data } = await axios.get(
    `${API_ROUTES.monitorClientes(empresaId)}&q=${q}`,
    { withCredentials: true }
  );
  return data.items;
};

export const fetchMesesMonitor = async (q = "") => {
  const { data } = await axios.get(`${API_ROUTES.monitorMeses}?q=${q}`, {
    withCredentials: true,
  });
  return data.items;
};

export const fetchDetalleMonitor = async (params = {}) => {
  const urlParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      urlParams.append(key, value);
    }
  }
  const { data } = await axios.get(
    `${API_ROUTES.monitorDetalle}?${urlParams.toString()}`,
    { withCredentials: true }
  );
  return data;
};

export const fetchDetallePrecioLlamada = async ({
  acctSessionId,
  acctStartTime,
  mes,
}) => {
  try {
    const params = new URLSearchParams({ acctSessionId, acctStartTime, mes });
    const { data } = await axios.get(
      `${API_ROUTES.monitorDetallePrecio}?${params.toString()}`,
      { withCredentials: true }
    );
    return data;
  } catch (error) {
    console.error("Error al obtener el detalle de la llamada:", error);
    return null;
  }
};

export const crearEmpresaClienteUsuario = async (payload) => {
  try {
    const { data } = await axios.post(API_ROUTES.listadoCrearTodo, payload, {
      withCredentials: true,
    });
    return data;
  } catch (error) {
    console.error("Error al crear empresa/cliente/usuario:", error);
    throw error;
  }
};

export async function enviarPerfilTarifas(tarifas) {
  try {
    const response = await axios.post(
      API_ROUTES.listadoCrearPerfilTarifas,
      tarifas,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error("Error al guardar tarifas:", error);
    throw error;
  }
}

/**  Resumen por sesión o por empresa (ADMIN) */
export async function fetchEmpresaResumen({ adminEmpresaId = null } = {}) {
  const url = adminEmpresaId
    ? API_ROUTES.adminFinanzas.resumenEmpresa(adminEmpresaId)
    : API_ROUTES.resumenEmpresaSesion;
  const { data } = await axios.get(url, { withCredentials: true });
  return data;
}

/** Movimientos por sesión o por empresa (ADMIN) */
export async function fetchMovimientosSesion(input) {
  const isObj = typeof input === "object" && input !== null;
  const clienteId = isObj ? input.clienteId : input;
  const adminEmpresaId = isObj ? input.adminEmpresaId ?? null : null;

  if (adminEmpresaId) {
    const url = API_ROUTES.adminFinanzas.movimientos(
      adminEmpresaId,
      clienteId ?? undefined
    );
    const { data } = await axios.get(url, { withCredentials: true });
    return data;
  }

  let url = API_ROUTES.movimientosSesion;
  if (clienteId !== undefined && clienteId !== null && `${clienteId}` !== "") {
    const cid = Number(clienteId);
    url = `${url}?cliente_id=${encodeURIComponent(String(cid))}`;
  }
  const { data } = await axios.get(url, { withCredentials: true });
  return data;
}

// alias conservado
export const fetchEmpresaMovimientos = (arg) => fetchMovimientosSesion(arg);
